/**
 * DuplicateCandidates — V2.0.79 (Feb 2026, Chunking #10)
 *
 * Superadmin (cross-workspace) / Admin (own-workspace) page listing
 * groups of candidate rows that share the same `dedup_key` (email OR
 * phone-last-10 SHA-256). Each group can be expanded to see every
 * duplicate row with owner + workspace + stage — so an agency admin
 * can spot the same person sitting in 3 recruiters' Kanbans.
 *
 * Backed by GET /api/superadmin/candidate-duplicates.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import {
    Copy, UsersThree, ArrowLeft, MagnifyingGlass, ArrowRight, CaretDown, CaretRight,
} from "@phosphor-icons/react";

const badge = "text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5";

function GroupCard({ group, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    const primary = group.primary || {};
    return (
        <div
            data-testid={`dup-group-${group.dedup_key.slice(0, 8)}`}
            className="border border-[#E5E5E5] bg-white"
        >
            <button
                type="button"
                data-testid={`dup-group-toggle-${group.dedup_key.slice(0, 8)}`}
                onClick={() => setOpen((o) => !o)}
                className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-[#FAFAFA] transition-colors"
            >
                <div className="pt-0.5">
                    {open ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-heading font-black text-base text-ink truncate">
                            {primary.name || "—"}
                        </p>
                        <span className={`${badge} bg-amber-100 text-amber-800`}>
                            {group.count}× duplicate
                        </span>
                        {group.unique_workspaces > 1 && (
                            <span className={`${badge} bg-rose-100 text-rose-800`}>
                                {group.unique_workspaces} workspaces
                            </span>
                        )}
                        {group.unique_owners > 1 && (
                            <span className={`${badge} bg-sky-100 text-sky-800`}>
                                {group.unique_owners} owners
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-[#525252] mt-1 truncate">
                        {primary.email || "(no email)"}
                        {primary.phone ? ` · ${primary.phone}` : ""}
                        {primary.current_title ? ` · ${primary.current_title}` : ""}
                    </p>
                </div>
                <div className="shrink-0 pt-1">
                    <p className="font-mono text-[10px] text-[#525252]" title={group.dedup_key}>
                        {group.dedup_key.slice(0, 8)}…
                    </p>
                </div>
            </button>
            {open && (
                <div className="border-t border-[#E5E5E5] divide-y divide-[#F0F0F0]">
                    {group.candidates.map((c, idx) => (
                        <div
                            key={c.id}
                            data-testid={`dup-candidate-${group.dedup_key.slice(0, 8)}-${idx}`}
                            className="px-4 py-2.5 flex items-start gap-3 text-sm"
                        >
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-bold text-ink">{c.name || "—"}</span>
                                    {c.code && (
                                        <span className="font-mono text-[10px] text-[#A3A3A3]">{c.code}</span>
                                    )}
                                    {c.stage && (
                                        <span className={`${badge} bg-[#F5F5F5] text-[#525252]`}>{c.stage}</span>
                                    )}
                                    {c.is_team_pool && (
                                        <span className={`${badge} bg-emerald-100 text-emerald-800`}>Team pool</span>
                                    )}
                                </div>
                                <p className="text-xs text-[#525252] mt-0.5">
                                    <span className="font-bold">{c.workspace_name || c.workspace_id}</span>
                                    {c.owner_name ? ` · Owned by ${c.owner_name}` : c.is_team_pool ? "" : " · Unassigned"}
                                    {c.location ? ` · ${c.location}` : ""}
                                </p>
                                <p className="text-[10px] text-[#A3A3A3] mt-1 font-mono">
                                    Added {c.created_at?.slice(0, 10) || "—"}
                                    {c.updated_at && c.updated_at !== c.created_at ? ` · Updated ${c.updated_at.slice(0, 10)}` : ""}
                                </p>
                            </div>
                            <Link
                                to={`/candidates?q=${encodeURIComponent(c.email || c.name || "")}`}
                                data-testid={`dup-candidate-open-${group.dedup_key.slice(0, 8)}-${idx}`}
                                className="shrink-0 text-cobalt hover:text-ink transition-colors text-xs font-bold flex items-center gap-1"
                                title="Open in Candidates list"
                            >
                                Open <ArrowRight size={12} weight="bold" />
                            </Link>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function DuplicateCandidates() {
    const [groups, setGroups] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [reloadKey, setReloadKey] = useState(0);
    const [q, setQ] = useState("");
    const [debouncedQ, setDebouncedQ] = useState("");

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(q), 350);
        return () => clearTimeout(t);
    }, [q]);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setError("");
        const params = new URLSearchParams();
        params.set("limit", "100");
        if (debouncedQ) params.set("q", debouncedQ);
        api.get(`/superadmin/candidate-duplicates?${params.toString()}`)
            .then((r) => {
                if (!mounted) return;
                setGroups(r.data?.groups || []);
                setTotal(r.data?.total_groups || 0);
            })
            .catch((e) => {
                logError("Duplicate candidates load failed:", e);
                if (!mounted) return;
                setError(e?.response?.data?.detail || "Couldn't load duplicates — please retry.");
                setGroups([]);
                setTotal(0);
            })
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, [debouncedQ, reloadKey]);

    return (
        <div className="max-w-6xl mx-auto px-6 py-8" data-testid="duplicates-page">
            <Link
                to="/dashboard"
                className="inline-flex items-center gap-1 text-xs text-[#525252] hover:text-ink font-bold uppercase tracking-wider mb-6 transition-colors"
                data-testid="duplicates-back-link"
            >
                <ArrowLeft size={12} weight="bold" /> Dashboard
            </Link>
            <div className="flex items-start gap-4 mb-8">
                <div className="border border-ink p-2.5">
                    <Copy size={22} weight="fill" className="text-ink" />
                </div>
                <div className="flex-1">
                    <h1 className="font-heading font-black text-3xl md:text-4xl text-ink leading-none">
                        Duplicate people
                    </h1>
                    <p className="text-sm text-[#525252] mt-2">
                        Candidates that appear multiple times — grouped by email or phone.
                        Same person across workspaces or recruiters is a good sign your team
                        is discovering the same talent — but a great signal to consolidate
                        outreach before the candidate gets a duplicate ping.
                    </p>
                </div>
            </div>

            <div className="mb-6 border-2 border-ink bg-white flex items-center px-3 py-2 gap-3">
                <MagnifyingGlass size={16} weight="bold" className="text-[#A3A3A3]" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search name / email / phone…"
                    className="flex-1 outline-none text-sm bg-transparent"
                    data-testid="duplicates-search"
                />
                {loading && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-cobalt">
                        Loading…
                    </span>
                )}
            </div>

            <div className="flex items-center gap-4 mb-4 text-xs text-[#525252]">
                <span className="flex items-center gap-1 font-bold">
                    <UsersThree size={14} weight="bold" />
                    {total} {total === 1 ? "group" : "groups"}
                </span>
                {q !== debouncedQ && <span className="text-cobalt">Filtering…</span>}
            </div>

            {!loading && error ? (
                <div
                    data-testid="duplicates-error"
                    className="border-2 border-rose-500 bg-rose-50 px-4 py-4 flex items-start gap-3"
                    role="alert"
                >
                    <div className="flex-1">
                        <p className="font-bold text-sm text-rose-800">Couldn't load duplicates</p>
                        <p className="text-xs text-rose-700 mt-1">{error}</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setReloadKey((k) => k + 1)}
                        data-testid="duplicates-retry"
                        className="shrink-0 text-xs font-bold uppercase tracking-wider bg-rose-800 text-white px-3 py-1.5 hover:bg-rose-900 transition-colors"
                    >
                        Retry
                    </button>
                </div>
            ) : !loading && groups.length === 0 ? (
                <div
                    data-testid="duplicates-empty"
                    className="border border-dashed border-[#E5E5E5] bg-[#FAFAFA] px-6 py-16 text-center"
                >
                    <p className="font-heading font-black text-lg text-ink">Nothing duplicated yet.</p>
                    <p className="text-xs text-[#525252] mt-2">
                        Once two rows share the same email or phone hash, they'll appear here.
                    </p>
                </div>
            ) : (
                <div className="space-y-3" data-testid="duplicates-list">
                    {groups.map((g, idx) => (
                        <GroupCard key={g.dedup_key} group={g} defaultOpen={idx === 0} />
                    ))}
                </div>
            )}
        </div>
    );
}
